import React, { useState, useEffect, useRef } from "react";
import config from "../../../../config/config.json";
import { apiCall } from "../../../../_services/apiCall";
import ParentExcel from "./parentexcel";

const Analytics = () => {
  const [answers, setAnswers] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(true);
  const [fetchingAnswers, setFetchingAnswers] = useState(false);
  const [financialYear, setFinancialYear] = useState([]);
  const [financialYearId, setFinancialYearId] = useState(null);
  const isMounted = useRef(true);

  useEffect(() => {
    // Load financial years on component mount
    loadFinancialYears();

    return () => {
      isMounted.current = false;
    };
  }, []);

  // Fetch data when financial year changes
  useEffect(() => {
    if (financialYearId) {
      fetchData(financialYearId);
    }
  }, [financialYearId]);

  const loadFinancialYears = async () => {
    try {
      // Check if data exists in local storage
      const storedData = localStorage.getItem('financialYearData');
      
      if (storedData && isMounted.current) {
        // Data exists in local storage, parse and use it
        const parsedData = JSON.parse(storedData);
        setFinancialYear(parsedData);
        // Set the latest financial year as default
        const latestYear = parsedData[parsedData.length - 1];
        if (latestYear) {
          setFinancialYearId(latestYear.id);
        }
      } else {
        // Data not in local storage, call API
        const { isSuccess, data } = await apiCall(
          `${config.POSTLOGIN_API_URL_COMPANY}getFinancialYear`,
          {},
          {}
        );
        
        if (isSuccess && isMounted.current) {
          // Store the response in local storage for future use
          localStorage.setItem('financialYearData', JSON.stringify(data.data));
          setFinancialYear(data.data);
          
          // Set the latest financial year as default
          const latestYear = data.data[data.data.length - 1];
          if (latestYear) {
            setFinancialYearId(latestYear.id);
          }
        }
      }
    } catch (error) {
      console.error("Error loading financial years:", error);
    }
  };

  const fetchFrameworkApi = async () => {
    const { isSuccess, data } = await apiCall(
      `${config.POSTLOGIN_API_URL_COMPANY}getFramework`,
      {},
      { type: "ALL" }
    );

    if (isSuccess && isMounted.current) {
      const frameworkIds = data?.data.map((item) => item.id);
      return frameworkIds;
    }
    return [];
  };

  const fetchData = async (currentFinancialYearId) => {
    if (!currentFinancialYearId) return;
    
    try {
      setLoadingQuestions(true);
      setAnswers([]); // Clear previous answers
      
      // Step 1: Fetch framework IDs
      const frameworkIds = await fetchFrameworkApi();
  
      // Step 2: Fetch questions
      const questionResponse = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}getReportingQuestion`,
        {},
        { financialYearId: currentFinancialYearId, frameworkIds },
        "GET"
      );
  
      // Check if the response data is an array or an object
      let tmpData = questionResponse.data;
  
      // Ensure the data is an array before attempting to sort
      if (Array.isArray(tmpData)) {
        tmpData.sort((a, b) => {
          if (a.serialNumber == null) return 1; // Move null/undefined to last
          if (b.serialNumber == null) return -1;
          return a.serialNumber - b.serialNumber;
        });
  
        setQuestions(tmpData);
        setLoadingQuestions(false);
  
        if (tmpData.length === 0) {
          throw new Error("No questions found");
        }
  
        // Step 3: Fetch all answers in one API call
        setFetchingAnswers(true);
        
        const answerResponse = await apiCall(
          `${config.POSTLOGIN_API_URL_COMPANY}getReportingQuestionAnswerBasedFinancialYear`,
          {},
          { financialYearId: currentFinancialYearId },
          "GET"
        );
  
        if (answerResponse.isSuccess) {
          setAnswers(answerResponse.data?.answers || []);
        }
  
        setFetchingAnswers(false);
      } else if (typeof tmpData === "object" && tmpData !== null) {
        // If data is an object, convert to an array
        const questionsArray = Object.values(tmpData.data || {});
        questionsArray.sort((a, b) => {
          if (a.serialNumber == null) return 1;
          if (b.serialNumber == null) return -1;
          return a.serialNumber - b.serialNumber;
        });
  
        setQuestions(questionsArray);
        setLoadingQuestions(false);
  
        if (questionsArray.length === 0) {
          throw new Error("No questions found");
        }
  
        // Step 3: Fetch all answers in one API call
        setFetchingAnswers(true);
        
        const answerResponse = await apiCall(
          `${config.POSTLOGIN_API_URL_COMPANY}getReportingQuestionAnswerBasedFinancialYear`,
          {},
          { financialYearId: currentFinancialYearId },
          "GET"
        );
  
        if (answerResponse.isSuccess) {
          setAnswers(answerResponse.data?.answers || []);
        }
  
        setFetchingAnswers(false);
      } else {
        console.error("Unexpected data format:", tmpData);
        throw new Error("Unexpected data format");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setLoadingQuestions(false);
      setFetchingAnswers(false);
    }
  };

  const handleFinancialYearChange = async (newFinancialYearId) => {
    setFinancialYearId(newFinancialYearId);
    // Data will be fetched automatically by the useEffect
  };

  // if (loadingQuestions) {
  //   return <div>Loading questions...</div>;
  // }

  return (
    <div className="container mx-auto p-4">
      {/* <h1 className="text-2xl font-bold mb-4">Entered values</h1> */}
      {fetchingAnswers && (
        <div className="flex items-center space-x-2 text-blue-500">
          <div
            style={{
              width: "3rem",
              height: "3rem",
              border: "2px solid #3b82f6",
              borderTop: "2px solid transparent",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
            }}
          ></div>
        </div>
      )}
      <ParentExcel 
        allAnswers={answers} 
        allQuestions={questions}
        financialYear={financialYear}
        financialYearId={financialYearId}
        onFinancialYearChange={handleFinancialYearChange}
      />
      <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
    </div>
  );
};

export default Analytics;